'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

const PhoneIcon = () =>
<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
  </svg>;


export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
          }
        });
      },
      { threshold: 0.1 }
    );
    const elements = heroRef?.current?.querySelectorAll('[data-animate]');
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex flex-col items-center justify-center pt-36 pb-24 overflow-hidden"
      style={{ background: 'var(--color-paper)' }}>
      
      {/* Blobs */}
      <div
        className="blob float-slow"
        style={{
          width: 'clamp(300px, 45vw, 600px)',
          height: 'clamp(300px, 45vw, 600px)',
          top: '10%',
          left: '5%',
          background: 'radial-gradient(circle, rgba(13,13,13,0.04) 0%, transparent 70%)',
          filter: 'blur(60px)'
        }} />
      
      <div
        className="blob float-slow-delay"
        style={{
          width: 'clamp(250px, 35vw, 500px)',
          height: 'clamp(250px, 35vw, 500px)',
          bottom: '15%',
          right: '8%',
          background: 'radial-gradient(circle, rgba(13,13,13,0.05) 0%, transparent 70%)',
          filter: 'blur(80px)'
        }} />
      
      {/* Arch Cards */}
      <div
        className="absolute top-24 left-1/2 -translate-x-1/2 w-full pointer-events-none"
        style={{ maxWidth: '1100px', height: '380px' }}
        aria-hidden="true">
        
        {[
        { left: '3%', top: '68%', rotate: '-36deg', src: 'https://img.rocket.new/generatedImages/rocket_gen_img_1a30e9264-1767671550454.png', alt: 'HVAC technician' },
        { left: '13%', top: '38%', rotate: '-24deg', src: 'https://images.unsplash.com/photo-1624643840046-87f1d4e6faa5', alt: 'Locksmith' },
        { left: '24%', top: '14%', rotate: '-14deg', src: 'https://img.rocket.new/generatedImages/rocket_gen_img_14d905a69-1768661539895.png', alt: 'Roofer' },
        { left: '37%', top: '4%', rotate: '-4deg', src: 'https://images.unsplash.com/photo-1676210134188-4c05dd172f89', alt: 'Plumber' },
        { left: '50%', top: '4%', rotate: '4deg', src: 'https://img.rocket.new/generatedImages/rocket_gen_img_1882fe9c4-1764727269391.png', alt: 'AC unit' },
        { left: '63%', top: '14%', rotate: '14deg', src: 'https://images.unsplash.com/photo-1658244475772-3fb0cd80e59e', alt: 'Car key' },
        { left: '75%', top: '38%', rotate: '24deg', src: 'https://img.rocket.new/generatedImages/rocket_gen_img_19f866c32-1771896149987.png', alt: 'Aerial roof' },
        { left: '85%', top: '68%', rotate: '36deg', src: 'https://img.rocket.new/generatedImages/rocket_gen_img_11c8c04d2-1772209925689.png', alt: 'Plumber pipe' }]?.
        map((card, i) =>
        <div
          key={i}
          className="arch-card pointer-events-auto"
          style={{ left: card?.left, top: card?.top, transform: `rotate(${card?.rotate})` }}>
          
            <img
            alt={card?.alt}
            loading="lazy"
            width={130}
            height={160}
            className="w-full h-full object-cover"
            src={card?.src} />
          
          </div>
        )}
      </div>
      {/* Hero Content */}
      <div
        className="max-w-4xl mx-auto px-5 relative z-10 text-center mt-44"
        style={{ transition: 'transform 0.8s cubic-bezier(0.2,0.8,0.2,1)' }}>
        
        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-[rgba(13,13,13,0.08)] mb-10 animate-fade-in-up">
          
          <span className="pulse-dot w-2 h-2 rounded-full bg-ink block" />
          <span className="text-[11px] font-bold tracking-[0.22em] uppercase text-ink-muted">
            Serving the Greater US &middot; Available 24/7
          </span>
        </div>

        <h1
          className="font-sans font-extrabold leading-[0.9] tracking-tightest mb-8 text-ink animate-fade-in-up-delay-1"
          style={{ fontSize: 'clamp(3rem, 9vw, 7.5rem)' }}>
          
          Your Home,<br />
          <span className="font-serif italic font-normal text-ink-muted">Fixed Fast.</span>
        </h1>

        <p
          className="text-lg md:text-xl text-ink-muted max-w-xl mx-auto leading-relaxed mb-12 animate-fade-in-up-delay-2">
          
          HVAC, Locksmith, Roofing, Plumbing &mdash; four trusted services, one call away. Licensed pros dispatched to your door.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up-delay-3">
          <Link
            href="/homepage#services"
            className="inline-flex items-center justify-center gap-2 border border-[rgba(13,13,13,0.15)] bg-transparent text-ink px-9 py-5 rounded-full text-base font-bold hover:bg-paper-warm transition-all duration-300 hover:border-[rgba(13,13,13,0.3)]">
            
            Explore Services
          </Link>
        </div>

        <div className="mt-16 flex flex-col items-center gap-3 opacity-50">
          <span className="text-[10px] uppercase tracking-[0.5em] text-ink-muted font-bold">Scroll</span>
          <div className="w-px h-12 bg-ink/10 overflow-hidden relative">
            <div className="absolute top-0 left-0 w-full h-1/2 bg-ink scroll-line-anim" />
          </div>
        </div>
      </div>
    </section>);

}